import React, { useEffect, useState } from "react";
import OnboardHeading from "../../components/OnboardHeading";
import BackButtonComponent from "../../components/backButton";
import "./style.scss";
import DebitFilter from "../../components/DebitFilter";
import DebitTable from "../../components/DebitTable";
import { toast } from "react-toastify";
import { autocloseTiming } from "../../api/regex";
import Loader from "../../components/Loader";
import { baseURL, getCurrentUser, postData } from "../../api";
import moment from "moment";

const DebitFilterReports = () => {
  const [tableData, setTableData] = useState([]);
  const [, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [apiCallsInProgress, setApiCallsInProgress] = useState(0);
  const [filterOptions, setFilterOptions] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({
    end_date: "",
    start_date: "",
    transaction_type: "",
    date_range: "",
    payment_event_uuid: "",
  });
  const [showFilter, setShowFilter] = useState(true);
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [csvLoading, setCsvLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [sortOrder, setSortOrder] = useState("desc");
  const [appliedFilters, setAppliedFilters] = useState(null);

  const handleApiStart = () => {
    setApiCallsInProgress((prev) => prev + 1);
    setLoading(true);
  };

  const handleApiEnd = () => {
    setApiCallsInProgress((prev) => {
      const newCount = prev - 1;
      if (newCount === 0) {
        setLoading(false);
      }
      return newCount;
    });
  };
  const handleGetUserInfo = async () => {
    handleApiStart();
    const res = await postData("get_user_details", {}, {});
    try {
      if (res?.status === true) {
        setLoading(false);
        setUserInfo(res?.data);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
    } finally {
      handleApiEnd();
    }
  };

  useEffect(() => {
    handleGetUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTableData = async () => {
    handleApiStart();
    const payLoad = {
      client_id: userInfo?.client_id,
    };
    try {
      const res = await postData("company-debit-report", {}, payLoad);
      if (res.status === true) {
        setTableData(res.data);
        const uniqueTypes = [
          ...new Set(res?.data?.map((item) => item.transaction_type)),
        ];
        const options = uniqueTypes.map((type) => ({
          label: type,
          value: type,
        }));
        const today = moment().startOf("day");
        const uniqueDateRanges = [
          ...new Set(
            res.data.map((item) => {
              const startDate = moment(item.start_date);
              const endDate = moment(item.end_date);

              // Skip if both start and end are future
              if (startDate.isAfter(today) && endDate.isAfter(today)) {
                return null;
              }

              return `${startDate.format("MM-DD-YYYY")} - ${endDate.format(
                "MM-DD-YYYY"
              )}`;
            })
          ),
        ].filter(Boolean);

        const dateOptions = uniqueDateRanges.map((range) => ({
          label: range,
          value: range,
        }));
        setDateRange(dateOptions);
        setFilterOptions(options);
      } else {
        toast.error(res.message, {
          theme: "colored",
          autoClose: autocloseTiming,
        });
      }
    } catch (err) {
      console.log(err);
      toast.error(err, {
        theme: "colored",
        autoClose: autocloseTiming,
      });
    } finally {
      handleApiEnd();
    }
  };

  useEffect(() => {
    if (userInfo?.client_id) {
      getTableData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setSelectedOptions((prev) => {
      if (id === "start_date") {
        return {
          ...prev,
          start_date: value,
          end_date: "",
        };
      }
      return {
        ...prev,
        [id]: value,
      };
    });
  };
  const handleTransactionTypeChange = (selected) => {
    setSelectedOptions((prev) => ({
      ...prev,
      transaction_type: selected?.value || "",
    }));
  };

  const handleDateRangeChange = (selected) => {
    const dateRangeValue = selected?.value || "";
    const paymentEventUuid = tableData.find((item) => {
      const itemStart = item.start_date;
      const itemEnd = item.end_date;
      return (
        `${moment(itemStart).format("MM-DD-YYYY")} - ${moment(itemEnd).format(
          "MM-DD-YYYY"
        )}` === dateRangeValue
      );
    })?.payment_event_uuid;

    setSelectedOptions((prev) => ({
      ...prev,
      date_range: dateRangeValue,
      payment_event_uuid: paymentEventUuid || "",
    }));
  };
  const handleClearFilter = () => {
    setSelectedOptions({
      end_date: "",
      start_date: "",
      transaction_type: "",
      date_range: "",
    });
    setFilteredTableData([]);
    setIsFilterApplied(false);
  };

  const handleFilter = () => {
    const { start_date, end_date, transaction_type, date_range } =
      selectedOptions;

    const filtered = tableData.filter((item) => {
      const paymentDate = moment(item.payment_date);
      const startDateMatch =
        start_date !== ""
          ? paymentDate.isSameOrAfter(moment(start_date))
          : true;
      const endDateMatch =
        end_date !== "" ? paymentDate.isSameOrBefore(moment(end_date)) : true;

      const transactionTypeMatch =
        transaction_type !== ""
          ? item.transaction_type === transaction_type
          : true;

      const dateRangeMatch =
        date_range !== ""
          ? `${moment(item.start_date).format("MM-DD-YYYY")} - ${moment(
              item.end_date
            ).format("MM-DD-YYYY")}` === date_range
          : true;

      return (
        startDateMatch && endDateMatch && transactionTypeMatch && dateRangeMatch
      );
    });

    setFilteredTableData(filtered);
    setAppliedFilters({ ...selectedOptions }); // ✅ Save applied filters here
    setIsFilterApplied(true);
  };

  const today = moment().format("YYYY-MM-DD");
  const tableDataArray = (
    isFilterApplied ? filteredTableData : tableData
  ).filter((row) => moment(row.payment_date).format("YYYY-MM-DD") <= today);

  const handleExport = async (type) => {
    if (type === "csv") {
      setCsvLoading(true);
    } else {
      setPdfLoading(true);
    }
    const payLoadInitialData = {
      end_date: moment().format("YYYY-MM-DD"),
      start_date: "",
      transaction_type: "",
      date_range: "",
      client_id: userInfo?.client_id,
      type: type,
      sort_type: sortOrder,
    };
    const payLoadFilteredData = {
      ...appliedFilters,
      client_id: userInfo?.client_id,
      type: type,
      sort_type: sortOrder,
    };

    const payload =
      isFilterApplied && appliedFilters
        ? payLoadFilteredData
        : payLoadInitialData;

    try {
      const response = await fetch(`${baseURL}company-debit-download`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getCurrentUser()}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const filenameMatch = contentDisposition?.match(/filename="?(.+)"?/);
      const filename = filenameMatch?.[1] || `report.${type}`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("File downloaded successfully", {
        theme: "colored",
        autoClose: 5000,
      });
    } catch (err) {
      console.error("Error downloading file:", err);
      toast.error("Failed to export file", {
        theme: "colored",
        autoClose: autocloseTiming,
      });
    } finally {
      if (type === "csv") {
        setCsvLoading(false);
      } else {
        setPdfLoading(false);
      }
    }
  };

  return (
    <>
      {apiCallsInProgress > 0 ? (
        <Loader />
      ) : (
        <div className="px-0 px-md-3">
          <OnboardHeading title="DEBIT REPORT" />
          <>
            <BackButtonComponent hasHistoryback />
          </>
          <>
            <DebitFilter
              filterOptions={filterOptions}
              dateRange={dateRange}
              handleDateRangeChange={handleDateRangeChange}
              handleTransactionTypeChange={handleTransactionTypeChange}
              handleChange={handleChange}
              selectedOptions={selectedOptions}
              handleClearFilter={handleClearFilter}
              handleFilter={handleFilter}
              showFilter={showFilter}
              setShowFilter={setShowFilter}
            />
          </>
          <>
            <DebitTable
              tableData={tableDataArray}
              handleExport={handleExport}
              csvLoading={csvLoading}
              pdfLoading={pdfLoading}
              setSortOrder={setSortOrder}
              sortOrder={sortOrder}
            />
          </>
        </div>
      )}
    </>
  );
};

export default DebitFilterReports;
